import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Info, Building2, Mail, Phone, MapPin } from "lucide-react";
import { InstagramIcon, FacebookIcon, XIcon, TikTokIcon, WhatsAppIcon, TelegramIcon } from "@/components/BrandIcons";

const Row = ({ icon: Icon, label, required, hint, children }) => (
  <div className="grid sm:grid-cols-12 gap-4 items-start">
    <div className="sm:col-span-4 flex items-start gap-2 pt-2">
      <Icon className="w-4 h-4 mt-0.5 text-zinc-500 shrink-0" aria-hidden="true" />
      <div>
        <Label className="text-sm font-semibold text-zinc-800">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        {hint && <div className="mt-0.5 text-xs text-zinc-500">{hint}</div>}
      </div>
    </div>
    <div className="sm:col-span-8">{children}</div>
  </div>
);

const VisibilityToggle = ({ checked, onChange, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 ${
      checked ? "bg-emerald-600" : "bg-zinc-300"
    }`}
  >
    <span
      aria-hidden="true"
      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform ${
        checked ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

export default function VenueInfo() {
  const [venue, setVenue] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await http.get("/venues/me");
      setVenue(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (k, v) => setVenue((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setError("");
    if (!venue.name || !venue.name.trim()) {
      setError("Venue name is required.");
      return;
    }
    setSaving(true);
    try {
      await http.patch("/venues/me", {
        name: venue.name,
        contact_email: venue.contact_email || null,
        contact_phone: venue.contact_phone || null,
        address: venue.address || null,
        instagram_url: venue.instagram_url || null,
        facebook_url: venue.facebook_url || null,
        x_url: venue.x_url || null,
        tiktok_url: venue.tiktok_url || null,
        whatsapp_url: venue.whatsapp_url || null,
        telegram_url: venue.telegram_url || null,
        show_email_public: !!venue.show_email_public,
        show_phone_public: !!venue.show_phone_public,
        show_address_public: !!venue.show_address_public,
        show_instagram_public: !!venue.show_instagram_public,
        show_facebook_public: !!venue.show_facebook_public,
        show_x_public: !!venue.show_x_public,
        show_tiktok_public: !!venue.show_tiktok_public,
        show_whatsapp_public: !!venue.show_whatsapp_public,
        show_telegram_public: !!venue.show_telegram_public,
      });
      toast.success("Venue info updated");
      load();
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Could not save";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!venue) return <div className="p-10 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 lg:p-10 max-w-4xl">
      <div className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Venue info</div>
      <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl tracking-tight">Update your venue details</h1>
      <p className="mt-2 text-sm text-zinc-500 max-w-2xl">
        Your venue name is shown publicly on every event page. Address is included on every ticket confirmation. Email and phone are optional — toggle whether each shows on your public venue page.
      </p>

      <div className="mt-8 rounded-xl bg-white border border-zinc-200 divide-y divide-zinc-200">
        {/* NAME */}
        <div className="p-6">
          <Row icon={Building2} label="Venue name" required hint="Always shown publicly on event pages and tickets.">
            <Input
              value={venue.name || ""}
              onChange={(e) => update("name", e.target.value)}
              data-testid="venueinfo-name"
              className="h-11"
              required
            />
          </Row>
        </div>

        {/* EMAIL */}
        <div className="p-6">
          <Row icon={Mail} label="Venue email address" hint="Where buyers can reach you with questions.">
            <div className="space-y-3">
              <Input
                type="email"
                value={venue.contact_email || ""}
                onChange={(e) => update("contact_email", e.target.value)}
                data-testid="venueinfo-email"
                placeholder="hello@yourvenue.com"
                className="h-11"
              />
              <div className="flex items-center gap-3">
                <VisibilityToggle
                  checked={!!venue.show_email_public}
                  onChange={(v) => update("show_email_public", v)}
                  testId="venueinfo-email-public"
                />
                <div className="text-sm text-zinc-600">
                  Show on public venue page
                </div>
              </div>
            </div>
          </Row>
        </div>

        {/* PHONE */}
        <div className="p-6">
          <Row icon={Phone} label="Contact number">
            <div className="space-y-3">
              <Input
                type="tel"
                value={venue.contact_phone || ""}
                onChange={(e) => update("contact_phone", e.target.value)}
                data-testid="venueinfo-phone"
                placeholder="+44 20 1234 5678"
                className="h-11"
              />
              <div className="flex items-center gap-3">
                <VisibilityToggle
                  checked={!!venue.show_phone_public}
                  onChange={(v) => update("show_phone_public", v)}
                  testId="venueinfo-phone-public"
                />
                <div className="text-sm text-zinc-600">
                  Show on public venue page
                </div>
              </div>
            </div>
          </Row>
        </div>

        {/* ADDRESS */}
        <div className="p-6">
          <Row icon={MapPin} label="Address" hint="Shown on every ticket confirmation.">
            <div className="space-y-3">
              <Textarea
                value={venue.address || ""}
                onChange={(e) => update("address", e.target.value)}
                data-testid="venueinfo-address"
                rows={3}
                placeholder={"123 Example Street\nLondon\nSW1A 1AA"}
              />
              <div className="flex items-center gap-3">
                <VisibilityToggle
                  checked={!!venue.show_address_public}
                  onChange={(v) => update("show_address_public", v)}
                  testId="venueinfo-address-public"
                />
                <div className="text-sm text-zinc-600">
                  Also show on public venue page (always included on tickets)
                </div>
              </div>
            </div>
          </Row>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 text-xs text-zinc-500">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>Fields marked * are required and displayed publicly on all your event pages.</span>
      </div>

      {/* SOCIAL */}
      <div className="mt-10">
        <h2 className="font-display font-bold text-xl tracking-tight">Social profiles</h2>
        <p className="mt-1 text-sm text-zinc-500 max-w-2xl">Paste the full URL for each profile. Toggle whether it appears as an icon on your public venue page.</p>

        <div className="mt-4 rounded-xl bg-white border border-zinc-200 divide-y divide-zinc-200">
          {[
            { key: "instagram", label: "Instagram", Icon: InstagramIcon, placeholder: "https://instagram.com/yourvenue", color: "text-pink-600" },
            { key: "facebook", label: "Facebook", Icon: FacebookIcon, placeholder: "https://facebook.com/yourvenue", color: "text-blue-600" },
            { key: "x", label: "X (Twitter)", Icon: XIcon, placeholder: "https://x.com/yourvenue", color: "text-zinc-900" },
            { key: "tiktok", label: "TikTok", Icon: TikTokIcon, placeholder: "https://tiktok.com/@yourvenue", color: "text-zinc-900" },
          ].map(({ key, label, Icon, placeholder, color }) => (
            <div key={key} className="p-6">
              <Row
                icon={({ className }) => <span className={`${color} ${className || ""}`}><Icon size={18} /></span>}
                label={label}
              >
                <div className="space-y-3">
                  <Input
                    value={venue[`${key}_url`] || ""}
                    onChange={(e) => update(`${key}_url`, e.target.value)}
                    data-testid={`venueinfo-${key}`}
                    placeholder={placeholder}
                    className="h-11"
                  />
                  <div className="flex items-center gap-3">
                    <VisibilityToggle
                      checked={!!venue[`show_${key}_public`]}
                      onChange={(v) => update(`show_${key}_public`, v)}
                      testId={`venueinfo-${key}-public`}
                    />
                    <div className="text-sm text-zinc-600">Show icon on public venue page</div>
                  </div>
                </div>
              </Row>
            </div>
          ))}
        </div>
      </div>

      {/* MESSAGING */}
      <div className="mt-10">
        <h2 className="font-display font-bold text-xl tracking-tight">Messaging</h2>
        <p className="mt-1 text-sm text-zinc-500 max-w-2xl">
          Direct chat links buyers can tap. For WhatsApp use <span className="font-mono">https://wa.me/44…</span>; for Telegram use <span className="font-mono">https://t.me/yourhandle</span>.
        </p>

        <div className="mt-4 rounded-xl bg-white border border-zinc-200 divide-y divide-zinc-200">
          {[
            { key: "whatsapp", label: "WhatsApp", Icon: WhatsAppIcon, placeholder: "https://wa.me/442012345678", color: "text-emerald-600" },
            { key: "telegram", label: "Telegram", Icon: TelegramIcon, placeholder: "https://t.me/yourvenue", color: "text-sky-600" },
          ].map(({ key, label, Icon, placeholder, color }) => (
            <div key={key} className="p-6">
              <Row
                icon={({ className }) => <span className={`${color} ${className || ""}`}><Icon size={18} /></span>}
                label={label}
              >
                <div className="space-y-3">
                  <Input
                    value={venue[`${key}_url`] || ""}
                    onChange={(e) => update(`${key}_url`, e.target.value)}
                    data-testid={`venueinfo-${key}`}
                    placeholder={placeholder}
                    className="h-11"
                  />
                  <div className="flex items-center gap-3">
                    <VisibilityToggle
                      checked={!!venue[`show_${key}_public`]}
                      onChange={(v) => update(`show_${key}_public`, v)}
                      testId={`venueinfo-${key}-public`}
                    />
                    <div className="text-sm text-zinc-600">Show icon on public venue page</div>
                  </div>
                </div>
              </Row>
            </div>
          ))}
        </div>
      </div>

      {error && <div className="mt-4 text-sm text-red-600">{error}</div>}

      <div className="mt-6 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="venueinfo-save"
          className="rounded-full bg-zinc-900 hover:bg-zinc-800 h-11 px-6"
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
